import type { Building } from "building-types/building/buildingTypes";
import type { OrthographicDrawingCamera, PerspectiveDrawingCamera } from "doc/plans/drawingCameraTypes";
import { DrawingCameraType } from "doc/plans/drawingCameraTypes";
import { aabb2Center } from "geom/aabb2";
import { aabb2Height } from "geom/aabb2Types";
import { aabb2Width } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import type { Vec3 } from "geom/vec3";
import { renderNDCBBFromBuilding } from "lib/house/render/renderNDCBBFromBuilding";
import { aabb2GrowByMultiplier, perspectiveCameraCropBasedOnNDCBB } from "./drawingCameraToMatrices";
import {
  PERSPECTIVE_DRAWING_AUTO_CROP_DEFAULT_MARGIN,
  PERSPECTIVE_DRAWING_AUTO_CROP_DEFAULT_FRACTIONAL_PADDING,
  PERSPECTIVE_DRAWING_VERTICAL_FOV_MAX,
} from "./perspectiveConstants";
import { AutoCrop } from "./sheetNode";

export async function perspectiveDrawingFinalCamera(
  building: Building,
  drawingCamera: PerspectiveDrawingCamera,
  autoCrop: AutoCrop,
  viewportHeight: number,
  autoCropPadding?: number,
): Promise<PerspectiveDrawingCamera> {
  if (autoCrop === AutoCrop.OFF) {
    return drawingCamera;
  }

  let paddingMultiplierY = 1.1;
  let paddingMultiplierX: number | undefined = undefined;
  if (autoCrop === AutoCrop.ON_FRACTIONAL_PADDING) {
    paddingMultiplierY = 1.0 + (autoCropPadding ?? PERSPECTIVE_DRAWING_AUTO_CROP_DEFAULT_FRACTIONAL_PADDING) * 2.0;
    paddingMultiplierX = paddingMultiplierY;
  } else if (autoCrop === AutoCrop.ON_INCHES_PADDING) {
    const autoCropPaddingInches = autoCropPadding ?? PERSPECTIVE_DRAWING_AUTO_CROP_DEFAULT_MARGIN;
    paddingMultiplierY = viewportHeight / (viewportHeight - autoCropPaddingInches * 2);
  }
  const buildingNDCBB = await renderNDCBBFromBuilding({
    building: building,
    camera: drawingCamera,
  });

  return perspectiveCameraCropBasedOnNDCBB(
    drawingCamera,
    buildingNDCBB,
    PERSPECTIVE_DRAWING_VERTICAL_FOV_MAX,
    paddingMultiplierY,
    paddingMultiplierX,
  );
}

export function fitCameraAroundWorldBB(
  aspect: number,
  paddingMultiplier: number,
  worldBB2: AABB2,
  eyePos: Vec3,
  upVector: Vec3 = [0, 0, 1],
): OrthographicDrawingCamera {
  const [centerX, centerY] = aabb2Center(worldBB2);
  worldBB2 = aabb2GrowByMultiplier(worldBB2, paddingMultiplier);

  let width = aabb2Width(worldBB2);
  let height = aabb2Height(worldBB2);

  // Preserve aspect ratio, but fit the whole world BB to view
  if (width / height > aspect) {
    height = width / aspect;
  } else {
    width = height * aspect;
  }

  const left = centerX - width * 0.5;
  const right = centerX + width * 0.5;
  const top = centerY + height * 0.5;
  const bottom = centerY - height * 0.5;
  return {
    type: DrawingCameraType.ORTHOGRAPHIC,
    eye: eyePos,
    target: [0, 0, 0],
    up: upVector,
    left,
    right,
    top,
    bottom,
    near: 0.1,
    far: 10000,
  };
}
